import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { AppDialog } from '../../components/AppDialog';
import { useNotify } from '../../components/notifications';

/** The most a formula can be: dbo.DailyQCLogBookBuilderSelected.FormulaText is
 *  nvarchar(100). Legacy edits it through an unlimited scratch field and lets you
 *  build a longer string than the column holds, so it truncates (or throws) on
 *  save; capping the box here means that cannot happen. */
const MAX_FORMULA = 100;

/** One pickable symbol — a Selected row that has been given a Symbol. */
export interface FormulaSymbol {
  oid: string;
  /** The row's field name, shown for context (legacy hides it, but picking blind
   *  between "T1" and "T2" is needlessly hard). */
  name?: string;
  symbol: string;
}

/**
 * The legacy "Formula Editor" — opened from the Formula cell of the Selected grid
 * on the Daily QC Log Book Builder detail.
 *
 * Three panes, as in G5: the SYMBOLS defined on this template (single-select), the
 * OPERATOR tokens from dbo.ReagentOperator, and the formula itself. Clicking either
 * list APPENDS its text to the end of the formula — legacy has no insert-at-caret,
 * no token backspace and no clear, so `T2>T1` is built by clicking T2, then >, then
 * T1. The textbox stays directly editable, which is the only way to correct a
 * mistake (also true of legacy).
 *
 * OK writes FormulaText, and the Formula checkbox follows from whether the text is
 * empty — the API derives the flag, so the two can never disagree. Legacy performs
 * NO validation of the string: it is stored verbatim and, in the whole legacy code
 * base, never parsed or evaluated. So this dialog deliberately does not invent a
 * grammar to enforce.
 */
export function FormulaEditorDialog({ open, initialText, symbols, readOnly = false, onCancel, onSave }: {
  open: boolean;
  /** The row's stored FormulaText ('' when it has none). */
  initialText: string;
  /** Symbols defined on THIS template's selected rows. */
  symbols: FormulaSymbol[];
  /** Show the formula without allowing a change — the record's read-only view.
   *  The popup still OPENS there, so the cell always responds. */
  readOnly?: boolean;
  onCancel: () => void;
  /** Receives the new formula text ('' to clear it). */
  onSave: (formulaText: string) => void;
}) {
  const notify = useNotify();
  const [text, setText] = useState(initialText);
  const [pickedSymbol, setPickedSymbol] = useState<string | null>(null);
  const [operators, setOperators] = useState<string[]>([]);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  // Re-seed each time the dialog opens, so re-opening a row shows what is stored
  // rather than whatever was last typed.
  useEffect(() => {
    if (!open) return;
    setText(initialText);
    setPickedSymbol(null);
  }, [open, initialText]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get('/api/v1/daily-qc-logbook/formula-operators');
        if (!cancelled) setOperators((res.data?.data ?? []) as string[]);
      } catch {
        if (!cancelled) notify.error('Failed to load the operator list.');
      }
    })();
    return () => { cancelled = true; };
  }, [open, notify]);

  /** Append a token, exactly as legacy does — to the END, no separator. */
  const append = (token: string) => {
    setText((t) => (t + token).slice(0, MAX_FORMULA));
    areaRef.current?.focus();
  };

  /** At the column's limit nothing more can be appended, so the pickers grey out
   *  instead of silently swallowing the click. (A warning would open a BLOCKING
   *  dialog on top of this one — §18.6 reroutes warnings to a modal.) */
  const atCap = text.length >= MAX_FORMULA || readOnly;

  return (
    <AppDialog
      open={open}
      title="Formula Editor"
      onClose={onCancel}
      width={720}
      footer={readOnly ? (
        <button type="button" className="ca-btn ca-btn-solid" onClick={onCancel}>Close</button>
      ) : (
        <>
          <button type="button" className="ca-btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={() => onSave(text.trim())}>OK</button>
        </>
      )}
    >
      <div className="fx-dual">
        {/* ── Symbols defined on this template ── */}
        <div className="fx-pane">
          <div className="fx-pane-h">Symbol</div>
          <div className="fx-list">
            {symbols.length === 0 && (
              <div className="fx-empty">
                No symbols yet — give a row a Symbol first, then it can be used here.
              </div>
            )}
            {symbols.map((s) => (
              <label key={s.oid} className="fx-row">
                <input
                  type="radio"
                  name="fx-symbol"
                  checked={pickedSymbol === s.oid}
                  disabled={atCap}
                  onChange={() => { setPickedSymbol(s.oid); append(s.symbol); }}
                />
                <span className="fx-sym">{s.symbol}</span>
                {s.name && <span className="fx-sub">{s.name}</span>}
              </label>
            ))}
          </div>
        </div>

        {/* ── Operator tokens (dbo.ReagentOperator) ── */}
        <div className="fx-pane">
          <div className="fx-pane-h">Operator</div>
          <div className="fx-list">
            {operators.map((op, i) => (
              <button
                key={`${op}-${i}`}
                type="button"
                className="fx-op"
                disabled={atCap}
                onClick={() => append(op)}
              >
                {op}
              </button>
            ))}
          </div>
        </div>

        {/* ── The formula ── */}
        <div className="fx-pane fx-pane--formula">
          <div className="fx-pane-h">Formula</div>
          <textarea
            ref={areaRef}
            className="fx-area"
            rows={10}
            maxLength={MAX_FORMULA}
            value={text}
            readOnly={readOnly}
            onChange={(e) => setText(e.target.value)}
            aria-label="Formula"
          />
          <div className="fx-hint">
            {readOnly
              ? 'Read-only — open the record with Edit to change the formula.'
              : `${text.length}/${MAX_FORMULA} — click a symbol or operator to append it, or type here.`}
          </div>
        </div>
      </div>
    </AppDialog>
  );
}
